import { CollectionMeta } from '@/clients/http'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { useEffect } from 'react'

type UseFetchListProps<TFilters, TData, TParams = {}, Model = any> = {
  hasPagination?: boolean
  pageSize?: number
  initialFilters: TFilters
  fetchFunction: Model
  buildQueryParams?: (filters: TFilters) => any
  buildParams?: (filters: TFilters) => TParams
  sort?: string | null
  register?: boolean
}

export interface UseFetchListForm<TFilters, TData = any, AdditionalData = any> {
  loading: boolean
  filters: TFilters
  filtersForm: TFilters
  pagination: { pageSize: number; currentPage: number; totalCount: number }
  meta: CollectionMeta<AdditionalData>
  data: TData[]
}

const convertToPayloadParam = (value: any) => {
  if (value === null || value === undefined || value === '') return undefined
  return value
}

export function useFetchList<TFilters, TData, TParams = {}, AdditionalData = any, Model = any>({
  hasPagination = true,
  pageSize = 20,
  initialFilters,
  fetchFunction,
  buildQueryParams = () => ({}),
  buildParams = () => ({}) as TParams,
  sort = null,
  register = true,
}: UseFetchListProps<TFilters, TData, TParams, Model>) {
  const formMethods = useForm<UseFetchListForm<TFilters, TData, AdditionalData>>({
    defaultValues: {
      filters: initialFilters,
      filtersForm: initialFilters,
      loading: true,
      pagination: { pageSize, currentPage: 1, totalCount: 0 },
      meta: {},
      data: [],
    },
  })

  const { watch, setValue } = formMethods
  const formValues = watch()
  const { loading, data = [], filters, pagination } = formValues

  const fetchData = async () => {
    setValue('loading', true)

    try {
      const queryParams = {
        ...(hasPagination
          ? {
              length: pagination.pageSize,
              page: pagination.currentPage,
            }
          : {}),
        sort: convertToPayloadParam(sort),
        ...buildQueryParams(filters),
      }

      const params = buildParams(filters)

      const response = await (fetchFunction as any)({ query: queryParams, params })

      if (response?.success) {
        // Handle the new backend response format
        // Backend returns: { success: true, message: "Done", data: { page, length, count, result } }
        setValue('data', response.data?.result ?? [])
        setValue('pagination.totalCount', response.data?.count ?? 0)

        // Build meta object for compatibility
        setValue('meta', {
          pagination: {
            totalCount: response.data?.count ?? 0,
            currentPage: response.data?.page ?? 1,
            pageSize: response.data?.length ?? pageSize,
          },
        })
      }

      if (response?.error) {
        toast.error(response?.error?.data?.message ?? 'An error occurred while fetching data')
      }
    } catch (error) {
      console.error('Fetch error:', error)
      toast.error('An unexpected error occurred')
    }

    setValue('loading', false)
  }

  const setPage = (page: number) => setValue('pagination.currentPage', page)
  const setPageSize = (size: number) => {
    setValue('pagination.pageSize', size)
    setPage(1)
  }

  const refresh = () => {
    if (!formValues?.loading) {
      fetchData()
    }
  }

  useEffect(() => {
    if (register) {
      fetchData()
    }
  }, [
    register,
    pagination.currentPage,
    pagination.pageSize,
    sort,
    ...Object.values(filters as any),
  ])

  return {
    formValues,
    formMethods,
    loading,
    data,
    pagination,
    filters,
    setPage,
    setPageSize,
    refresh,
  }
}
